import type { SimulationActivity } from '../../types/simulation';
import type { GeoJsonLineString, SegmentRouteMetadata } from '../../services/simulation/resultsPathsService';
import { activityStopKindForUi } from './inferActivityStopKind';
import { collectActivityIdCandidatesForTripFocus } from './tripFocusFromActivities';

/** One unique road path between a departure→arrival address pair (from `/results/{id}/paths`). */
export type AddressPairRoute = {
  routeId: string;
  geometry: GeoJsonLineString;
  metadata?: SegmentRouteMetadata;
};

/** The focused trip's onboard span on a shift, as 0-based segment indices (segment i spans activity i→i+1). */
type TripSegmentWindow = { shiftId: string; segStart: number; segEnd: number };

export function haversineDistanceMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
): number {
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const earthRadiusMeters = 6_371_000;
  const dLat = toRadians(bLat - aLat);
  const dLng = toRadians(bLng - aLng);
  const lat1 = toRadians(aLat);
  const lat2 = toRadians(bLat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

function extractActivityIndex(activity: SimulationActivity): number | null {
  const a = activity as Record<string, unknown>;
  return (
    toFiniteNumber(a.min_driver_activity_number) ??
    toFiniteNumber(a.max_driver_activity_number) ??
    toFiniteNumber(a.stop_number)
  );
}

export function activityMatchesTripKeys(
  activity: SimulationActivity,
  matchKeys: Set<string> | string[] | null
): boolean {
  if (!matchKeys) return false;
  const keySet = matchKeys instanceof Set ? matchKeys : new Set(matchKeys);
  if (keySet.size === 0) return false;
  return collectActivityIdCandidatesForTripFocus(activity).some((k) => keySet.has(k));
}

export function parseShiftSegmentRouteId(routeId: string): { shiftId: string; segmentIndex: number } | null {
  const idx = routeId.lastIndexOf(':');
  if (idx <= 0 || idx >= routeId.length - 1) return null;
  const shiftId = routeId.slice(0, idx).trim();
  const segmentRaw = routeId.slice(idx + 1).trim();
  const segmentIndex = Number(segmentRaw);
  if (!shiftId || !Number.isFinite(segmentIndex) || segmentIndex < 0) return null;
  return { shiftId, segmentIndex: Math.floor(segmentIndex) };
}

export function deriveTripSegmentWindowFromActivities(
  matchKeys: string[] | null,
  activities: SimulationActivity[] | undefined
): TripSegmentWindow | null {
  if (!matchKeys?.length || !activities?.length) return null;
  const matching = activities.filter((a) => activityMatchesTripKeys(a, matchKeys));
  if (matching.length === 0) return null;

  const shiftCounts = new Map<string, number>();
  const pickupByShift = new Map<string, number[]>();
  const dropByShift = new Map<string, number[]>();

  for (const act of matching) {
    const rawShift = (act as Record<string, unknown>).vehicle_shift_id ?? (act as Record<string, unknown>).vehicleShiftId;
    if (typeof rawShift !== 'string' || rawShift.trim() === '') continue;
    const shiftId = rawShift.trim();
    shiftCounts.set(shiftId, (shiftCounts.get(shiftId) ?? 0) + 1);
    const idx = extractActivityIndex(act);
    if (idx == null) continue;
    const kind = activityStopKindForUi(act);
    if (kind === 'pickup') {
      const list = pickupByShift.get(shiftId) ?? [];
      list.push(idx);
      pickupByShift.set(shiftId, list);
    } else if (kind === 'dropoff') {
      const list = dropByShift.get(shiftId) ?? [];
      list.push(idx);
      dropByShift.set(shiftId, list);
    }
  }

  const candidateShifts = Array.from(shiftCounts.keys());
  if (candidateShifts.length === 0) return null;
  // Prefer shifts that contain both pickup and dropoff for this trip.
  let focusShift = candidateShifts.find((sid) => (pickupByShift.get(sid)?.length ?? 0) > 0 && (dropByShift.get(sid)?.length ?? 0) > 0) ?? null;
  if (!focusShift) {
    focusShift = candidateShifts.sort((a, b) => (shiftCounts.get(b) ?? 0) - (shiftCounts.get(a) ?? 0))[0] ?? null;
  }
  if (!focusShift) return null;

  const pickupVals = pickupByShift.get(focusShift) ?? [];
  const dropVals = dropByShift.get(focusShift) ?? [];
  if (pickupVals.length === 0 || dropVals.length === 0) return null;

  const pickupIdx = Math.min(...pickupVals);
  const dropoffIdx = Math.max(...dropVals);
  if (!Number.isFinite(pickupIdx) || !Number.isFinite(dropoffIdx) || dropoffIdx < pickupIdx) return null;

  const segStart = Math.max(0, Math.floor(pickupIdx - 1));
  const segEnd = Math.max(segStart, Math.floor(dropoffIdx - 1));
  return { shiftId: focusShift, segStart, segEnd };
}

export function vehicleRouteMatchesTrip(
  route: AddressPairRoute,
  matchKeys: string[] | null,
  tripRidePoint: Record<string, unknown> | null | undefined,
  _tripShiftIds: Set<string>,
  activities: SimulationActivity[] | undefined
): boolean {
  if (!matchKeys?.length) return false;
  const keySet = new Set(matchKeys);
  const onboardIds = route.metadata?.rides_onboard_ids ?? [];
  // (A) Onboard manifest is AUTHORITATIVE when present: the route belongs to this trip iff the
  // trip's ride is listed onboard for this leg. Approach/departure legs (rider not yet / no longer
  // aboard) list other rides or none, so they are correctly excluded — and we do NOT fall through
  // to the looser heuristics below, which would re-add non-onboard segments.
  if (onboardIds.length > 0) {
    return onboardIds.some((id) => keySet.has(id));
  }

  // (B) No onboard manifest — derive the trip's [pickup, dropoff] activity span and match only
  // segments that lie STRICTLY within it (rider genuinely aboard), not merely overlapping it.
  // Overlap would include the leg arriving at the pickup and the leg departing after the dropoff.
  // Segment i spans activity i -> i+1; window.segStart = pickupIdx-1, window.segEnd = dropoffIdx-1,
  // so the onboard legs are segment indices [segStart+1, segEnd] (activity numbers [pickup, dropoff]).
  const window = deriveTripSegmentWindowFromActivities(matchKeys, activities);
  if (window) {
    if (
      route.metadata?.vehicle_shift_id === window.shiftId &&
      route.metadata?.from_driver_activity_number != null &&
      route.metadata?.to_driver_activity_number != null
    ) {
      const segFrom = route.metadata.from_driver_activity_number;
      const segTo = route.metadata.to_driver_activity_number;
      if (segFrom >= window.segStart + 1 && segTo <= window.segEnd + 1) {
        return true;
      }
    }
    const parsed = parseShiftSegmentRouteId(route.routeId);
    if (
      parsed &&
      parsed.shiftId === window.shiftId &&
      parsed.segmentIndex >= window.segStart + 1 &&
      parsed.segmentIndex <= window.segEnd
    ) {
      return true;
    }
  }

  // (C) Flat per-ride payloads: the routeId IS the ride id (exact, then substring for an id
  // embedded in a composite routeId). Only reached when there is no onboard manifest.
  const rid = route.routeId;
  const lowerRid = rid.toLowerCase();
  const strongKeys = matchKeys.filter((k) => k.trim().length >= 6);
  if (matchKeys.some((k) => rid === k)) return true;
  if (strongKeys.some((k) => lowerRid.includes(k.toLowerCase()))) return true;
  if (
    tripRidePoint &&
    typeof tripRidePoint.from_latitude === 'number' &&
    typeof tripRidePoint.from_longitude === 'number' &&
    typeof tripRidePoint.to_latitude === 'number' &&
    typeof tripRidePoint.to_longitude === 'number' &&
    route.geometry.coordinates.length >= 2
  ) {
    const coords = route.geometry.coordinates;
    const [lng0, lat0] = coords[0];
    const [lng1, lat1] = coords[coords.length - 1];
    const thr = 280;
    const near = (lat: number, lng: number, plat: number, plng: number) =>
      haversineDistanceMeters(lat, lng, plat, plng) < thr;
    if (
      (near(lat0, lng0, tripRidePoint.from_latitude, tripRidePoint.from_longitude) &&
        near(lat1, lng1, tripRidePoint.to_latitude, tripRidePoint.to_longitude)) ||
      (near(lat1, lng1, tripRidePoint.from_latitude, tripRidePoint.from_longitude) &&
        near(lat0, lng0, tripRidePoint.to_latitude, tripRidePoint.to_longitude))
    ) {
      return true;
    }
  }
  return false;
}

export function mergeRoadRoutesForTripGradient(
  routes: AddressPairRoute[],
  tripRidePoint: Record<string, unknown> | null | undefined
): [number, number][] {
  if (routes.length === 0) return [];
  const routePositions = routes
    .map((route) => route.geometry.coordinates.map(([lng, lat]) => [lat, lng] as [number, number]))
    .filter((positions) => positions.length >= 2);
  if (routePositions.length === 0) return [];

  const pickup =
    tripRidePoint &&
    typeof tripRidePoint.from_latitude === 'number' &&
    typeof tripRidePoint.from_longitude === 'number'
      ? ([tripRidePoint.from_latitude, tripRidePoint.from_longitude] as [number, number])
      : null;
  const dropoff =
    tripRidePoint &&
    typeof tripRidePoint.to_latitude === 'number' &&
    typeof tripRidePoint.to_longitude === 'number'
      ? ([tripRidePoint.to_latitude, tripRidePoint.to_longitude] as [number, number])
      : null;

  const oriented = (positions: [number, number][], reverse: boolean): [number, number][] =>
    reverse ? [...positions].reverse() : positions;
  const startDistScore = (positions: [number, number][]): number => {
    if (!pickup && !dropoff) return 0;
    const start = positions[0]!;
    const end = positions[positions.length - 1]!;
    const fromScore = pickup ? haversineDistanceMeters(start[0], start[1], pickup[0], pickup[1]) : 0;
    const toScore = dropoff ? haversineDistanceMeters(end[0], end[1], dropoff[0], dropoff[1]) : 0;
    return fromScore + toScore;
  };

  let startIndex = 0;
  let startReverse = false;
  let bestScore = Number.POSITIVE_INFINITY;
  routePositions.forEach((positions, idx) => {
    const forwardScore = startDistScore(positions);
    if (forwardScore < bestScore) {
      bestScore = forwardScore;
      startIndex = idx;
      startReverse = false;
    }
    const reverseScore = startDistScore([...positions].reverse());
    if (reverseScore < bestScore) {
      bestScore = reverseScore;
      startIndex = idx;
      startReverse = true;
    }
  });

  const used = new Set<number>([startIndex]);
  let merged = [...oriented(routePositions[startIndex]!, startReverse)];
  const joinThresholdMeters = 220;

  while (used.size < routePositions.length) {
    const currentEnd = merged[merged.length - 1]!;
    let nextIndex = -1;
    let nextReverse = false;
    let bestJoinDistance = Number.POSITIVE_INFINITY;

    routePositions.forEach((positions, idx) => {
      if (used.has(idx) || positions.length < 2) return;
      const forwardStart = positions[0]!;
      const reverseStart = positions[positions.length - 1]!;
      const dForward = haversineDistanceMeters(currentEnd[0], currentEnd[1], forwardStart[0], forwardStart[1]);
      const dReverse = haversineDistanceMeters(currentEnd[0], currentEnd[1], reverseStart[0], reverseStart[1]);
      if (dForward < bestJoinDistance) {
        bestJoinDistance = dForward;
        nextIndex = idx;
        nextReverse = false;
      }
      if (dReverse < bestJoinDistance) {
        bestJoinDistance = dReverse;
        nextIndex = idx;
        nextReverse = true;
      }
    });

    if (nextIndex < 0 || bestJoinDistance > joinThresholdMeters) break;
    const next = oriented(routePositions[nextIndex]!, nextReverse);
    const nextStart = next[0]!;
    const overlapDistance = haversineDistanceMeters(currentEnd[0], currentEnd[1], nextStart[0], nextStart[1]);
    if (overlapDistance < 10) {
      merged = [...merged, ...next.slice(1)];
    } else {
      merged = [...merged, ...next];
    }
    used.add(nextIndex);
  }

  return merged;
}
